/**
 * Filtry strony na telefonie (<md): rzad chipow z aktywnymi wartosciami
 * (kazdy otwiera wlasny wybierak) + przycisk „Filtry · N” z pelnym formularzem
 * w arkuszu od dolu. Na desktopie renderuje `children` bez zadnej zmiany.
 * Stan filtrow zostaje tam, gdzie byl (URL strony) — komponent tylko go
 * prezentuje. Wzorzec: `.claude/skills/farmaster-ui-standard/references/mobile.md`.
 */
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import * as React from "react";
import { BottomSheet, SheetFooterActions } from "@/components/ui/bottom-sheet";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export interface MobileFilterChip {
	id: string;
	/** Nazwa filtra — tytul arkusza chipa i tekst dla czytnikow. */
	label: string;
	/** Biezaca wartosc pokazana na chipie (np. „Własne”, „sty–paź”). */
	value: React.ReactNode;
	/** Wartosc rozna od domyslnej: chip wyrozniony i liczony w „Filtry · N”. */
	active?: boolean;
	/** Wlasny wybierak chipa; bez niego chip otwiera pelny arkusz filtrow. */
	picker?: React.ReactNode;
}

interface MobileFiltersProps {
	/** Formularz desktopowy — na desktopie jedyna rzecz, ktora sie renderuje. */
	children: React.ReactNode;
	chips: MobileFilterChip[];
	/** Formularz w arkuszu (jedna kolumna); domyslnie `children`. */
	sheetContent?: React.ReactNode;
	/** Przywraca domyslne filtry strony (przycisk „Wyczyść”). */
	onClear?: () => void;
	/** Tekst glownego przycisku arkusza, np. „Pokaż 104 apteki”. */
	resultLabel?: string;
	/**
	 * Strony z jawnym „Pokaż raport”: wywolywane przez glowny przycisk arkusza
	 * (i arkusza chipa) tuz przed jego zamknieciem. Bez tego filtry dzialaja na zywo.
	 */
	onPrimary?: () => void;
	/** Dodatkowe akcje na koncu rzedu chipow (np. odswiezenie). */
	actions?: React.ReactNode;
	className?: string;
}

export function MobileFilters({
	children,
	chips,
	sheetContent,
	onClear,
	resultLabel = "Pokaż wyniki",
	onPrimary,
	actions,
	className,
}: MobileFiltersProps) {
	const isBelowMd = useIsBelowMd();
	const [sheetOpen, setSheetOpen] = React.useState(false);
	const [chipId, setChipId] = React.useState<string | null>(null);
	if (!isBelowMd) return <>{children}</>;

	const activeCount = chips.filter((c) => c.active).length;
	const openChip = chips.find((c) => c.id === chipId && c.picker);

	return (
		<>
			<div
				data-testid="mobile-filter-bar"
				className={cn(
					"flex shrink-0 items-center gap-1.5 border-b border-border bg-card px-2.5 py-2",
					className,
				)}
			>
				{/* `relative`: etykiety `sr-only` (absolute) musza zostac w przewijanym
				    rzedzie, inaczej rozpychaja cala strone w bok. */}
				<div className="relative flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
					{chips.map((chip) => (
						<button
							key={chip.id}
							type="button"
							data-testid={`mobile-filter-chip-${chip.id}`}
							onClick={() => (chip.picker ? setChipId(chip.id) : setSheetOpen(true))}
							className={cn(
								"inline-flex h-9 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-[13px] whitespace-nowrap text-foreground",
								chip.active && "border-primary/40 bg-primary/10 text-primary",
							)}
						>
							<span className="sr-only">{chip.label}: </span>
							<span className="max-w-[11rem] truncate">{chip.value}</span>
							<ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
						</button>
					))}
				</div>
				{actions}
				<button
					type="button"
					data-testid="mobile-filters-open"
					onClick={() => setSheetOpen(true)}
					className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[13px] font-medium text-foreground"
				>
					<SlidersHorizontal className="size-4 text-muted-foreground" aria-hidden />
					Filtry
					{activeCount > 0 && <span className="text-primary">· {activeCount}</span>}
				</button>
			</div>
			<BottomSheet
				open={sheetOpen}
				onOpenChange={setSheetOpen}
				title="Filtry"
				data-testid="mobile-filters-sheet"
				footer={
					<SheetFooterActions
						onClear={onClear}
						primaryLabel={resultLabel}
						onPrimary={() => {
							onPrimary?.();
							setSheetOpen(false);
						}}
					/>
				}
			>
				<div className={mobileFormClass}>{sheetContent ?? children}</div>
			</BottomSheet>
			{openChip && (
				<BottomSheet
					open
					onOpenChange={(open) => {
						if (!open) setChipId(null);
					}}
					title={openChip.label}
					data-testid="mobile-filter-chip-sheet"
					footer={
						<SheetFooterActions
							primaryLabel={resultLabel}
							onPrimary={() => {
								onPrimary?.();
								setChipId(null);
							}}
						/>
					}
				>
					<div className={mobileFormClass}>{openChip.picker}</div>
				</BottomSheet>
			)}
		</>
	);
}

/** Formularz w arkuszu: jedna kolumna, pola 40 px. */
const mobileFormClass =
	"flex flex-col gap-3 pt-1 [&_select]:h-10 [&_select]:text-sm [&_input:not([type=checkbox]):not([type=radio])]:h-10 [&_input]:text-sm";

/** Pole formularza w arkuszu filtrow: etykieta nad kontrolka. */
export function MobileFilterField({
	label,
	children,
	className,
}: {
	label: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div className={cn("flex min-w-0 flex-col gap-1", className)}>
			<span className="text-xs text-muted-foreground">{label}</span>
			{children}
		</div>
	);
}
